import {uniq} from 'lodash';

import {Exceptions} from '../exceptions/exceptions.class';
import {HtmlContent, PrismicImage} from '../cms/cms.types';
import {Order} from '../orders/order.class';
import {PriceObject, PriceUnit, WebPrice} from '../pricing/price.types';
import {ShareLinkRecipient} from '../share-link/share-link.class';
import {User} from '../users/user.class';

export const prettyItemIDCaptureGroup = `([a-z0-9][0-9]{0,5}-?[0-9]?)`;
export const SHIPPING_SAVER = `8000000`;
export const SHIPPING_SAVER_PRICE = 119;
export const uglyItemIDCaptureGroup = `([a-z0-9][0-9]{6})`;
export type CrossRefLookupSource = `backsheet`;
export type WebcatSplitPosition = `Top` | `Bottom`;

export interface AdditionalSpecValue {
    name: string;
    value: string;
}

export interface AllBrands {
    numbers: BrandLink[];
    A: BrandLink[];
    B: BrandLink[];
    C: BrandLink[];
    D: BrandLink[];
    E: BrandLink[];
    F: BrandLink[];
    G: BrandLink[];
    H: BrandLink[];
    I: BrandLink[];
    J: BrandLink[];
    K: BrandLink[];
    L: BrandLink[];
    M: BrandLink[];
    N: BrandLink[];
    O: BrandLink[];
    P: BrandLink[];
    Q: BrandLink[];
    R: BrandLink[];
    S: BrandLink[];
    T: BrandLink[];
    U: BrandLink[];
    V: BrandLink[];
    W: BrandLink[];
    X: BrandLink[];
    Y: BrandLink[];
    Z: BrandLink[];
}

export interface AssortmentItem {
    binQty?: number;
    canShipAir?: boolean;
    categoryPath?: string;
    exceptions?: Exceptions;
    fullWebcat: string;
    id: string;
    isSupplierItem?: boolean;
    image: string;
    imagePath: string;
    lotTrack?: boolean;
    name: string;
    ormd?: boolean;
    prettyid: string;
    price?: string;
    priceC?: number;
    priceInfo?: PriceObject;
    prop65Message?: string;
    reorderQty: number;
    sdsLink?: string;
    upsHazard?: boolean;
    webcatName: string;
    xrefList?: XrefItem[];
}

export interface BrandLetter {
    brandLinks: BrandLink[];
    name: string;
    numHidden?: number;
}

export interface BrandLink {
    id: string;
    name: string;
    slug: string;
}

export interface Breadcrumbs {
    categoryPath: string;
    href: string;
    name: string;
}

export interface CardItem {
    active?: boolean;
    amtDiff?: number;
    bin?: string;
    binQty?: number;
    brand?: string;
    categoryPath?: string;
    checked?: boolean;
    comment?: string;
    compPrice?: number;
    description?: any;
    discontinued?: boolean;
    fullWebcat?: string;
    group?: number;
    hidden?: boolean;
    id?: string;
    imagePath?: string;
    isSupplierItem?: boolean;
    inStock?: boolean;
    itemNeedsAppr?: boolean;
    interval?: string;
    invnGroup?: string;
    invoiceNumber?: string;
    item?: string;
    lastInvcDt?: string;
    myItemNumber?: string;
    name?: string;
    orderCount?: number;
    orderDate?: string;
    orderNumber?: string;
    packageQuantity?: number;
    pctDiff?: number;
    pkgQty?: number;
    prettyid?: string | undefined;
    prettyItem?: string;
    price?: string | PriceObject;
    priceC?: number;
    priceInfo?: any;
    qtyLimit?: number;
    qtyOrd?: number;
    quantity?: number;
    replaced?: boolean;
    shortdesc?: any;
    uid?: string;
    units?: number;
    unitsOrdered?: number;
    webPrice?: any;
    wid?: number;
    widDescription?: string;
}

export interface ComparedSpec {
    name: string;
    values: string[];
}

export interface CompareItem {
    imagePath: string;
    itemNum: string;
}

export interface CompareItemsRes {
    orderableItems: OrderableItem[];
    specs: ComparedSpec[];
}

export interface CrossRefLookupItem {
    image: string;
    imagePath: string;
    item: string;
    name: string;
    pkgQty: number;
    prettyid: string;
    priceInfo?: PriceObject;
    xco: string;
    xitem: string;
}

interface CustomXrefItem {
    company?: string;
    description?: string;
    image?: string;
    xrefItem: string;
}

export interface FrequentlyBoughtTogether {
    items: OrderableItem[];
    itemTotal: number;
}

export interface GdspSdsAsset {
    _id: string;
    assetID: string;
    modDate: string;
}

export interface GetItemResponse {
    excludeSt: string[];
    id: string;
    image: string;
    name: string;
    pkgQty: number;
    popularity: number;
    prettyid: string;
    price?: string;
    priceUnit: PriceUnit;
    shortdesc: string;
    status?: number;
}

export interface IDPVariant {
    _id: string;
    children: IDPVariantChild[];
    field: string;
    name: string;
    productValue: string;
    rank: number;
    renderType: string;
}

export interface IDPVariantChild {
    _id: string;
    childID?: string;
    discontinued: `Yes` | `No`;
    dropdownAsset?: StepAsset;
    dropdownExtValue?: STEPExtrinsic;
    dropdownValue?: string;
    outOfStock?: boolean;
}

interface InternalInventoryData {
    data?: string;
    email?: {subject: string; to: string};
    label: string;
    phone?: {label: string; number: string; url: string};
    type: string;
    url?: string;
}

export interface ItemBreadcrumb {
    categoryPath: string;
    name: string;
}

export interface ItemImages {
    id: string;
    image: string;
    imagePath?: string;
    item: string;
    itemID_s?: string;
    secondaryImages?: WebAsset[];
}

export interface LookAheadLink {
    categoryPath: string;
    linkText: string;
}

export type ManyPrices = {
    itemTotal: number;
} & {
    [item: string]: PriceObject;
};

export interface MediaItem {
    comment: string;
    filename: string;
    type: string;
}

export interface OrderableItem {
    actualSizeImageAsset?: string;
    actualSizeImageHeight?: string;
    actualSizeImageWidth?: string;
    actualSizeRequired?: boolean;
    backsheetProductTitle?: string;
    binLocation?: string;
    brand?: string;
    categoryPath?: string;
    checked?: boolean;
    crossRefFrom?: string;
    description?: string;
    excludeStArray?: string[];
    fullWebcat?: string;
    gdspId?: string;
    groupDescription?: string;
    gSeq?: number;
    id?: string;
    image?: string;
    imagePath?: string;
    inStock?: boolean;
    invnGroup?: string;
    isBestSeller_b?: boolean;
    isDirectReplacement?: boolean;
    isDiscontinued?: boolean;
    isJustAdded?: boolean;
    isNew?: boolean;
    isPurchased?: boolean;
    isSampled?: boolean;
    item: string;
    onOrder?: boolean;
    packageQuantity?: number;
    pandemicItem?: boolean;
    pandemicItemBlocked?: boolean;
    pkgQty?: number;
    prettyItem?: string;
    price?: string;
    priceC?: number;
    priceInfo?: PriceObject;
    quantity?: number;
    recToken?: string;
    scmt?: string;
    subbedFrom?: string;
    unitsOrdered?: number;
    webPrice?: WebPrice;
    wid?: number;
    widDescription?: string;
    xitem?: string;

    // Error state
    status?: number;
    statusText?: string;
}

export interface OrderableItemsRes {
    items: OrderableItem[];
}

export interface PkgQtyVariant {
    id: string;
    outOfStock: boolean;
    qty: number;
}

export interface PriceInfo {
    bpriceC: number;
    bpriceCCalc: number;
    bpriceEA: number;
    bpricePkg: string;
    bpriceUnit: string;
    multiplier: number;
    priceBook: string;
    priceC: number;
    priceCCalc: number;
    priceEA: number;
    pricePkg: string;
    priceUnit: PriceUnit;
    specialComment: string;
    specialNumber: string;
    success: string;
}

interface PrintXrefItem {
    company: string;
    xrefItem: any; // string?
}

export interface PrismicCategoryContent {
    banner_medium: PrismicImage;
    banner_small: PrismicImage;
    description: HtmlContent[];
    expiration_date: string;
    last_publication_date: string;
    responsive_banner_image: PrismicImage;
    url: string;
    webcatid: string;
}

export interface ReStockEmailParams {
    account: string;
    email: string;
    item: string;
    login: string;
}

export interface ReStockEmailRes {
    email: string;
    msg: string[];
    result: string;
}

export interface ShareItemRes {
    msg: string[];
    result: string;
    sentTo: ShareLinkRecipient[];
}

export interface STEPExtrinsic {
    text: string;
    value: string;
    valueID: string;
}

export interface StepAsset {
    id: string;
    image: string;
    name: string;
}

export interface SwitchAndSaveItem extends OrderableItem {
    amtDiff: number;
    compPrice: number;
    pctDiff: number;
}

export interface TechSpecValue {
    facetName: string;
    facetValue: string;
}

export interface WebAsset {
    _id: string;
    description?: string;
    fileLocation: string;
    metaData: {
        extension: string;
        filename: string;
        format: string;
        mimeType: string;
        size: string;
        uploadDt: string;
    };
    name: string;
    referenceType: string;
    thumb1000Location?: string;
    thumb400Location?: string;
    thumbLocation?: string;
}

export interface WebcatMeta {
    breadcrumbs: Breadcrumbs[];
    categoryPath: string;
    children?: WebcatMeta[];
    cleanName: string;
    cmsContent?: PrismicCategoryContent;
    content?: string;
    description: string;
    fullCleanName: string;
    graphic_image_url?: string;
    h1Override?: string;
    hasItems: boolean;
    id: number;
    imageAssetLargeThumb: string;
    imagePath: string;
    isTerminal: boolean;
    keywords: string;
    lookAheadLinks: LookAheadLink[];
    metaDescOverride?: string;
    metaRobots?: string;
    name: string;
    parent: number;
    parents?: WebcatMeta[];
    renderView: string;
    showExt: string;
    sortOrder?: number;
    splitPosition?: WebcatSplitPosition;
    stepID: string;
    stepImage?: string;
    titleTagOverride?: string;
}

export interface XrefItem {
    company: string;
    description: string;
    image: string;
    xrefcompany: string;
    xrefItem: string;
}

export class Item {
    additionalSpecs?: AdditionalSpecValue[];
    alertMessage?: string;
    assortmentContains?: string[];
    assortmentItems?: AssortmentItem[];
    asstItems_s: string;
    baseBcTitle: string;
    baseBcURL: string;
    bin?: string;
    binLocation?: string;
    brand_name_s?: string;
    breadcrumbs: ItemBreadcrumb[];
    canPurchase: boolean;
    canShipAir: boolean;
    cat: string[];
    catalogPage: string;
    catalogPages_s_M: string[];
    catdesc: string[];
    checked?: boolean;
    ctry: string;
    customXrefItem?: CustomXrefItem;
    description: string;
    discontinued?: boolean;
    documents: string[];
    dropshipDays_i: number;
    error?: string; // Have not verified
    exceptions: Exceptions;
    excludeStArray: string[];
    expertItems?: OrderableItem[];
    ezid: number[];
    frequentlyBoughtTogether?: FrequentlyBoughtTogether;
    gdspSdsAsset: GdspSdsAsset;
    gdspType_s: string;
    graMsdsavail_b: string;
    green: boolean;
    group?: number;
    groupstring: string;
    GSDP_ID_s: string;
    h1HTML?: string;
    h1SeoOverride: string;
    hidden?: boolean;
    id: string;
    image: string;
    image_string: string;
    imagePath: string;
    images: string[];
    inAssortment: string[];
    inAssortmentsItems?: OrderableItem[];
    inStock: boolean;
    invAdmin?: InternalInventoryData[];
    invMsg: string;
    invngroup: string;
    invngroupdesc: string;
    invStatus: string;
    isBestSeller_b: boolean;
    isdiscontinue: boolean;
    isJustAdded?: boolean;
    isLabeledInShop?: any;
    isNew: boolean;
    isPurchased?: boolean;
    isWeb: boolean;
    item?: string;
    itemID_s?: string;
    itemNeedsAppr: boolean;
    itemQtyLimit_i?: number;
    itemtype: `D` | `I` | `L`;
    labelPicture_i: number;
    lastInvcDt?: string;
    lastPurchase?: string;
    lastPurchaseInvoice?: string;
    lottracking: boolean;
    material_spec: string;
    material_spec_s: string;
    material_uom: string;
    media: MediaItem[];
    metaDescription?: string;
    msds_s: string;
    msdsavail: boolean;
    name: string;
    name_no_numbers: string;
    npLinks?: string;
    ondemandDays_i?: number;
    onHand: string;
    onSpecial_s_M: string[];
    ormd: boolean;
    pageIncludes?: {
        IB?: {html: string};
        NT?: {html: string};
    };
    pageTitle?: string;
    pandemicItem: boolean;
    pandemicItemBlocked: boolean;
    pdc: number;
    pkgQty: number;
    pkgQtyVariants?: PkgQtyVariant[];
    popmult: number;
    popularity: number;
    prettyid: string;
    prettyItem?: string;
    price: PriceObject;
    priceInfo: PriceObject;
    priceSubject_b: boolean;
    pricingStatus: `` | `Blocked` | `Convert`;
    primaryImageDesc?: string;
    printXrefItem?: PrintXrefItem;
    productNameWildcard: string;
    prop65b_b: boolean;
    prop65c_b: boolean;
    prop65Message?: string;
    psection: boolean;
    publicXref: XrefItem[];
    purchasedBy_s_M: string[];
    purchases?: number;
    qty: number;
    qtyLimit?: number;
    qtyOrdered?: number;
    requiredItems?: OrderableItem[];
    sds_s?: string;
    sdsAssetFileName_s: string;
    sdsavail_b: boolean;
    secondaryImages?: WebAsset[];
    shortdesc: string;
    snbFilter_b?: boolean;
    specText_txt: string[];
    spell: string;
    startDt: string;
    statColor: `red` | `green` | ``;
    substituteFor_s_M: string[];
    supportingDocuments?: WebAsset[];
    surcharge?: boolean;
    switchAndSaveID?: string;
    switchAndSaveItem?: SwitchAndSaveItem;
    tariffCode_s: string;
    techSpecs: TechSpecValue[];
    termSpec?: {
        [extrinsic: string]: string;
    };
    trainingLinks?: {
        type: string;
        filename: string;
        comment: string;
    }[];
    TTCode_s: string;
    uglyItem: string;
    unitsOrdered?: number;
    upsHazard: boolean;
    variants?: IDPVariant[];
    webcatid: string;
    webPrice: WebPrice;
    weight_c_f: number;
    weight_ea_f: number;
    xref: string[];
    yourLabel?: any; // string?
    yourNumbers?: any[]; // string[]?

    constructor(itemData) {
        // Assign class properties
        for (const propName in itemData) {
            if (itemData.hasOwnProperty(propName)) {
                this[propName] = itemData[propName];
            }
        }

        // Transform data
        this._addPriceInfo();
        this._addPricingStatus();
        this._addYourXRefs();
        this._replaceOldTitle();
    }

    /**
     * Returns true if item can be auto-reordered
     */
    public canAutoReorder(user: User): boolean {
        // Do not allow punchout users
        if (user.isPunchout) {
            return false;
        }

        // Cannot auto-reorder discontinued items
        if (this.isdiscontinue) {
            return false;
        }

        // Cannot auto-reorder shipping saver (ICOM-2967)
        if (this.id === SHIPPING_SAVER) {
            return false;
        }

        // Can auto-reorder by default
        return true;
    }

    /**
     * TBD
     * @param orderableItems
     * @param removeConverts
     */
    public static formatItemsForDisplay(orderableItems: OrderableItem[], removeConverts?: boolean): OrderableItem[] {
        const itemsToDisplay = [];
        for (const orderableItem of orderableItems) {
            // Do not display BLOCKED and ERROR priced items
            if (orderableItem.priceInfo && (orderableItem.priceInfo.error === `BLOCKED` || orderableItem.priceInfo.errorType === `ERROR`)) {
                continue;
            }

            // Do not display CONVERTS
            if (
                orderableItem.priceInfo &&
                removeConverts &&
                (orderableItem.priceInfo.errorType === `CONVERT` || orderableItem.priceInfo.errorType === `ERROR`)
            ) {
                continue;
            }

            // Limit name to 50 characters
            const fiftyChars = 50;
            if (orderableItem.description && orderableItem.description.length > fiftyChars) {
                orderableItem.description = `${orderableItem.description.substring(0, fiftyChars)}...`;
            }

            // Add remaining updated items to itemsToDisplay
            itemsToDisplay.push(orderableItem);
        }
        return itemsToDisplay;
    }

    /**
     * Returns true if the item can be purchased
     * @param item
     */
    public static isPurchasable(item: Item): boolean {
        // Do not allow discontinued item without any remaining stock (Grainger we don't know stock)
        if (item.isdiscontinue && (item.inStock === false || item.itemtype === `D`)) {
            return false;
        }

        // Default to true
        return true;
    }

    /**
     * Tests if prettyItemId is valid
     * @param prettyItemId - prettyItemId to test
     */
    public static isValidPrettyItemID(prettyItemId: string): boolean {
        const validPrettyItemIDRegex = new RegExp(`^${prettyItemIDCaptureGroup}$`, `i`);
        return !!(prettyItemId && validPrettyItemIDRegex.test(prettyItemId));
    }

    /**
     * Tests if uglyItemId is valid
     * @param uglyItemId - uglyItemId to test
     */
    public static isValidUglyItemID(uglyItemId: string): boolean {
        const validUglyItemIDRegex = new RegExp(`^${uglyItemIDCaptureGroup}$`, `i`);
        return !!(uglyItemId && validUglyItemIDRegex.test(uglyItemId));
    }

    /**
     * Returns itemId in a way suitable for display
     * @param itemId
     */
    public static prettyItem(itemId: string): string {
        let charNotZero = false;
        let prettyItemNum = ``;
        let itemNumChar: string;
        for (let charPos = 0; charPos < itemId.length; charPos++) {
            itemNumChar = itemId.substring(charPos, charPos + 1);
            if (itemNumChar !== `0`) {
                charNotZero = true;
                if (charPos < 6) {
                    prettyItemNum = prettyItemNum + itemNumChar;
                } else {
                    prettyItemNum = prettyItemNum + (`-` + itemNumChar);
                }
            } else if (charNotZero === true && charPos < 6) {
                prettyItemNum = prettyItemNum + `0`;
            } else {
                prettyItemNum = prettyItemNum + ` `;
            }
        }
        return prettyItemNum.trim();
    }

    /**
     * Sorts provided assortment items by sortOrder
     * @param assortmentItems - Items to sort
     */
    public static sortAssortmentItems(assortmentItems: AssortmentItem[]) {
        const fieldName = `webcatName`;
        const sortedAssortmentItems = assortmentItems;

        // Sort assortmentItems
        sortedAssortmentItems.sort((assortmentItem1, assortmentItem2) => {
            if (
                (assortmentItem1[fieldName] != null) < (assortmentItem2[fieldName] != null) ||
                assortmentItem1[fieldName] < assortmentItem2[fieldName]
            ) {
                return -1;
            }
            if (
                (assortmentItem1[fieldName] != null) > (assortmentItem2[fieldName] != null) ||
                assortmentItem1[fieldName] > assortmentItem2[fieldName]
            ) {
                return 1;
            }
            return 0;
        });

        // Return sortedAssortmentItems
        return sortedAssortmentItems;
    }

    /**
     * Converts an item's prettyId to an uglyId
     * @param itemId - prettyId you wish to convert
     * @returns - Ugly version of the provided itemId
     */
    public static uglyItem(itemId: string): string {
        const imperialPrettyRegex = /^([A-Z0-9]{1,6})(-[A-Z0-9])?$/;
        const moreLikelyToBeImperial = /^([0-9]{1,6})$/;
        const graingerPartRegex = /^[0-9]{1,2}[A-Z0-9]{3}[0-9]$/;

        if (!itemId) {
            return ``;
        }

        itemId = itemId.toUpperCase().replace(`#`, ``);

        if (graingerPartRegex.test(itemId) && !moreLikelyToBeImperial.test(itemId)) {
            return itemId;
        }
        if (!imperialPrettyRegex.test(itemId)) {
            return itemId;
        }

        const regexResult = imperialPrettyRegex.exec(itemId);

        if (regexResult[2]) {
            regexResult[1] = regexResult[1] + regexResult[2].replace(`-`, ``);
        } else {
            regexResult[1] = regexResult[1] + `0`;
        }
        return `0000000`.substring(0, 7 - regexResult[1].length) + regexResult[1];
    }

    /**
     * Determines if the provided user can purchase this item
     * @param item - Item to evaluate
     * @param user - User to evaluate
     * @param order - Check if promo on order
     */
    public static userCanPurchase(item: Item, user: User, order: Order) {
        // Immediately disallow !canPurchase
        if (!item.canPurchase) {
            return false;
        }

        // Disallow punchout users performing an inspect operation
        if (user.isPunchout && user.operation === `inspect`) {
            return false;
        }

        // Disallow if item needs approval and user has a catalog message
        if (item.itemNeedsAppr && !!user.catalogMessage) {
            return false;
        }

        // Disallow promo items on the order
        if (order) {
            for (const orderLine of order.allLines) {
                if (orderLine.promo && orderLine.item === item.id) {
                    return false;
                }
            }
        }

        // Default to true
        return true;
    }

    /**
     * Adds the `priceInfo` property to make it work with PriceComponent
     * @private
     */
    private _addPriceInfo() {
        this.priceInfo = this.price;
    }

    /**
     * Evaluates and adds canPurchase, pricingStatus
     * @private
     */
    private _addPricingStatus() {
        // Defaults
        let priceCanPurchase = true;
        this.pricingStatus = ``;

        // Price Yes
        if (this.price) {
            priceCanPurchase = true;
        }

        // Price No
        if (this.price && this.price.errorType === `ERROR`) {
            priceCanPurchase = false;
        }

        // Convert
        if (this.price && this.price.errorType === `CONVERT` && this.price.error !== `BLOCKED`) {
            priceCanPurchase = true;
            this.pricingStatus = `Convert`;
        }

        // Block
        if (this.price && this.price.errorType === `CONVERT` && this.price.error === `BLOCKED`) {
            priceCanPurchase = false;
            this.pricingStatus = `Blocked`;
        }

        // Discontinued items without stock
        if (this.isdiscontinue && (this.inStock === false || this.itemtype === 'D')) {
            priceCanPurchase = false;
        }
        if (this.canPurchase !== false) {
            this.canPurchase = priceCanPurchase;
        }
    }

    /**
     * Adds yourLabel, yourNumbers (customer specific xrefs)
     * @private
     */
    private _addYourXRefs() {
        if (this.customXrefItem || this.printXrefItem) {
            const yourNumbers = [];
            if (this.customXrefItem) {
                yourNumbers.push(this.customXrefItem.xrefItem);
            }
            if (this.printXrefItem) {
                yourNumbers.push(this.printXrefItem.xrefItem);
                let yourLabel = this.printXrefItem.xrefItem;
                if (yourLabel.length < 25) {
                    const trimmedName = this.printXrefItem.company.substring(0, 24 - yourLabel.length);
                    yourLabel = `${trimmedName}-${this.printXrefItem.xrefItem}`;
                }
                this.yourLabel = yourLabel;
            }
            if (yourNumbers.length > 0) {
                this.yourNumbers = uniq(yourNumbers);
            }
        }
    }

    /**
     * Removes oldTitle information from description (ref ICOM-2340)
     * @private
     */
    private _replaceOldTitle() {
        const oldTitleRegex = /^(<br>)?<div class="prodtitle">.*?(?=(<\/div>))<\/div>/i;
        if (oldTitleRegex.test(this.description)) {
            this.description = this.description.replace(oldTitleRegex, ``);
        }
    }
}
